import {
  EMBROIDERY_PRICE_CATALOG_KEYS,
  EmbroideryPriceKey,
  priceKeyForItem,
} from '../pricing/embroideryPrices'
import { EmbroideryVariant } from '../useEmbroideryVariants'

import { EmbroideryItem } from './types'

export type V2EmbroideryVariants = {
  byPriceKey: Partial<Record<EmbroideryPriceKey, EmbroideryVariant>>
}

/**
 * The variant behind each price key. The pricing provider prices from this and the v2 flow buys
 * from it, so the price shown and the line added always agree.
 *
 * Pass variants already narrowed to the flow (e.g. `useEmbroideryPrices().embroideryVariants`).
 * That list drops the setup product, which carries no type tag, so it's passed on its own.
 */
export const resolveV2EmbroideryVariants = (
  embroideryVariants: readonly EmbroideryVariant[],
  setupVariant: EmbroideryVariant | undefined
): V2EmbroideryVariants => {
  const byPriceKey: V2EmbroideryVariants['byPriceKey'] = {}
  for (const [priceKey, catalogKey] of Object.entries(EMBROIDERY_PRICE_CATALOG_KEYS)) {
    // A key the catalog lists twice is ambiguous, so it resolves nothing rather than guessing.
    const matches = embroideryVariants.filter(candidate => candidate.key === catalogKey)
    if (matches.length === 1) byPriceKey[priceKey as EmbroideryPriceKey] = matches[0]
  }

  // A catalog without the chest and sleeve text products, like prod until they ship there, sells
  // that text as plain text, the same product the legacy flow buys.
  byPriceKey['text:chest'] ??= byPriceKey.text
  byPriceKey['text:sleeve'] ??= byPriceKey.text
  byPriceKey.logo_setup = setupVariant

  return { byPriceKey }
}

/** The variant an item is bought at; undefined when its price key has no product. */
export const variantForEmbroideryItem = (
  item: EmbroideryItem,
  variants: V2EmbroideryVariants
): EmbroideryVariant | undefined => {
  const priceKey = priceKeyForItem(item)
  return priceKey === undefined ? undefined : variants.byPriceKey[priceKey]
}
